import React, { useState } from 'react';
import {
  Box,
  Paper,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ApplicationDashboard from './ApplicationDashboard';

interface WelcomeDashboardProps {
  userDisplayName: string | null;
  onOpenApps: () => void;
  onOpenDocs: () => void;
}

const WelcomeDashboard: React.FC<WelcomeDashboardProps> = ({
  userDisplayName,
  onOpenApps,
  onOpenDocs
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
      {/* Hybrid: icon when collapsed, banner when expanded */}
      {!expanded ? (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="Show Dashboard" arrow placement="bottom">
            <IconButton
              onClick={() => setExpanded(true)}
              sx={{
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                color: 'primary.main',
                border: '2px solid',
                borderColor: 'primary.light',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: '0 8px 20px rgba(25, 118, 210, 0.12)',
                  background: 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)',
                }
              }}
            >
              <DashboardOutlinedIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box 
          sx={{ 
            mb: 2,
            py: 1.5,
            px: 3,
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.light',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
              borderColor: 'primary.light',
              boxShadow: '0 2px 12px rgba(25, 118, 210, 0.12)',
            }
          }}
          onClick={() => setExpanded(false)}
        >
          <Tooltip 
            title="Click to hide" 
            arrow 
            placement="bottom"
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.0,
              color: 'primary.main',
              transition: 'color 0.3s ease-in-out',
            }}>
              <DashboardIcon sx={{ fontSize: 24 }} />
              <Box sx={{ 
                fontSize: '0.95rem', 
                fontWeight: 600,
                letterSpacing: '0.02em'
              }}>
                Hide Dashboard
              </Box>
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Dashboard Content */}
      <Collapse in={expanded} unmountOnExit>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <ApplicationDashboard
            onFilterSelect={() => {
              // When a metric is clicked inside ApplicationDashboard, navigate to apps view
              onOpenApps();
            }}
          />
        </Paper>
      </Collapse>
    </Box>
  );
};

export default WelcomeDashboard;